import { Injectable, NgZone } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { get } from 'lodash';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { PollingConfig, PollingConfigResponseEvaluatorOperation } from '../_models/models';
import { EventService } from './event.service';

enum PollingResponseStatus {
  SUCCESS,
  ERROR,
  PENDING,
}

@Injectable({
  providedIn: 'root'
})
export class RequestPollingService {
  private readonly LOCAL_STORAGE_KEY = 'pollingSubscriptions';
  private pollingInterval = 5000; // Polling interval in milliseconds
  private pollingsMap: Map<string, PollingConfig> = new Map();
  private subscriptionsMap: Map<string, Subscription> = new Map();
  private pollingResponseEvaluator: Record<PollingConfigResponseEvaluatorOperation, Function> = {
    EQUALS: (a: any, b: any): boolean => a === b,
    IS_NOT: (a: any, b: any): boolean => a !== b,
  };

  constructor(
    private apiService: ApiService,
    private notificationService: NzNotificationService,
    private eventService: EventService,
    private ngZone: NgZone
  ) {
  }

  loadStoredPollings(): void {
    try {
      const serialized = localStorage.getItem(this.LOCAL_STORAGE_KEY);
      if (!serialized) {
        return;
      }
      const parsed: [string, PollingConfig][] = JSON.parse(serialized);
      this.pollingsMap = new Map(parsed);
      this.pollingsMap.forEach(config => {
        try {
          this.beginPolling(config);
        } catch (beginError) {
          console.error(`Failed to resume polling for id ${config.id}, dropping entry:`, beginError);
          this.pollingsMap.delete(config.id);
        }
      });
      this.storePollings();
    } catch (e) {
      console.error('Failed to load stored pollings, clearing corrupted state:', e);
      localStorage.removeItem(this.LOCAL_STORAGE_KEY);
    }
  }

  startPolling(config: PollingConfig): void {
    if (this.pollingsMap.has(config.id)) {
      return;
    }
    this.pollingsMap.set(config.id, config);
    try {
      this.beginPolling(config);
    } catch (beginError) {
      console.error(`Failed to start polling for id ${config.id}:`, beginError);
      this.pollingsMap.delete(config.id);
      return;
    }
    this.storePollings();
  }

  private beginPolling(config: PollingConfig): void {
    if (!config.startedAt) {
      config.startedAt = Date.now();
    }
    const interval = config.interval || this.pollingInterval;
    this.ngZone.runOutsideAngular(() => {
      this.subscriptionsMap.set(config.id, timer(0, interval).pipe(
        switchMap(() => this.apiService[config.apiMethod](...config.args))).subscribe({
        next: (res: unknown) => {
          const responseEvaluation = this.evaluateResponse(config.responseEvaluators, res);
          if (responseEvaluation === PollingResponseStatus.SUCCESS) {
            // TODO: use NzNotificationRef returned by notificationService to subscribe to the notification close/interaction events
            const successValue = String(get(res, config.responseEvaluators.success.path) ?? '');
            const safeSuccessValue = /^https?:\/\//i.test(successValue) ? successValue : '';
            this.notificationService.success(
              config.completionNotification.success.title,
              config.completionNotification.success.body.replace('{{value}}', () =>
                safeSuccessValue.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')),
              { nzDuration: 0 }
            );
            this.stopPolling(config.id);
          } else if (responseEvaluation === PollingResponseStatus.ERROR) {
            if (config.completionNotification.error) {
              this.notificationService.error(
                config.completionNotification.error.title,
                config.completionNotification.error.body,
                { nzDuration: 0 }
              );
            }
            this.stopPolling(config.id);
          } else if (config.maxDurationMs && (Date.now() - config.startedAt) >= config.maxDurationMs) {
            const expiredNotification = config.completionNotification.expired;
            if (expiredNotification) {
              this.notificationService.error(
                expiredNotification.title,
                expiredNotification.body,
                { nzDuration: 0 }
              );
            }
            this.stopPolling(config.id);
          }
        },
        error: (error) => {
          console.error('Error fetching data:', error);
          if (config.completionNotification.error) {
            this.notificationService.error(
              config.completionNotification.error.title,
              config.completionNotification.error.body,
              { nzDuration: 0 }
            );
          }
          this.stopPolling(config.id);
        },
      }));
    });
  }

  isPolling(id: string): boolean {
    return this.pollingsMap.has(id);
  }

  stopPolling(id: string): void {
    const config: PollingConfig = this.pollingsMap.get(id);
    if (config.completionNotification.notificationIdsToRemove?.length) {
      config.completionNotification.notificationIdsToRemove.forEach((notifId: string) => {
        this.notificationService.remove(notifId);
      });
    }
    const subscription = this.subscriptionsMap.get(id);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptionsMap.delete(id);
      this.pollingsMap.delete(id);
      this.storePollings();
    }
    if (config.completionEvent) {
      this.eventService.emitEvent(config.completionEvent.name, config.completionEvent.data || null);
    }
  }

  private evaluateResponse(responseEvaluators: PollingConfig['responseEvaluators'], res: unknown): PollingResponseStatus {
    const successParamFromPath = get(res, responseEvaluators.success.path);
    if (this.pollingResponseEvaluator[responseEvaluators.success.operation](successParamFromPath, responseEvaluators.success.compareTo)) {
      return PollingResponseStatus.SUCCESS;
    }
    const errorEvaluator = responseEvaluators.error;
    if (errorEvaluator) {
      const errorParamFromPath = get(res, errorEvaluator.path);
      if (this.pollingResponseEvaluator[errorEvaluator.operation](errorParamFromPath, errorEvaluator.compareTo)) {
        return PollingResponseStatus.ERROR;
      }
    }
    return PollingResponseStatus.PENDING;
  }

  private storePollings(): void {
    try {
      const serialized = JSON.stringify(Array.from(this.pollingsMap));
      localStorage.setItem(this.LOCAL_STORAGE_KEY, serialized);
    } catch (e) {
      console.error('Failed to persist polling state:', e);
    }
  }

}


